import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api, CONDITIONS } from "../lib/api";
import { ItemCard } from "../components/ItemCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [cats, setCats] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [local, setLocal] = useState({ price_min: params.get("price_min") || "", price_max: params.get("price_max") || "", brand: params.get("brand") || "", size: params.get("size") || "" });

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const query = Object.fromEntries([...params.entries()].filter(([, v]) => v));
    api.get("/search", { params: query }).then(({ data }) => setResult(data)).catch(() => setResult({ items: [], total: 0, pages: 0 }));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  const applyLocal = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    Object.entries(local).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    next.delete("page");
    setParams(next);
  };

  const page = parseInt(params.get("page") || "1");
  const input = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl sm:text-4xl font-bold mr-auto">{params.get("q") ? `„${params.get("q")}"` : "Alle Artikel"}</h1>
        <button data-testid="toggle-filters-btn" onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 border border-border rounded-full px-4 py-2 text-sm font-semibold hover:bg-secondary transition-colors">
          <SlidersHorizontal className="w-4 h-4" /> Filter
        </button>
        <Select value={params.get("sort") || "newest"} onValueChange={(v) => update("sort", v)}>
          <SelectTrigger data-testid="sort-select" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Neueste</SelectItem>
            <SelectItem value="price_asc">Preis aufsteigend</SelectItem>
            <SelectItem value="price_desc">Preis absteigend</SelectItem>
            <SelectItem value="popular">Beliebtheit</SelectItem>
            {params.get("q") && <SelectItem value="relevance">Relevanz</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      {showFilters && (
        <form onSubmit={applyLocal} className="mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 bg-card border border-border rounded-2xl p-4" data-testid="filter-panel">
          <Select value={params.get("category_id") || "all"} onValueChange={(v) => update("category_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Alle Kategorien</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={params.get("condition") || "all"} onValueChange={(v) => update("condition", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-condition"><SelectValue placeholder="Zustand" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Jeder Zustand</SelectItem>
              {Object.entries(CONDITIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <input data-testid="filter-price-min" className={input} type="number" min="0" placeholder="Preis von €" value={local.price_min} onChange={(e) => setLocal({ ...local, price_min: e.target.value })} />
          <input data-testid="filter-price-max" className={input} type="number" min="0" placeholder="Preis bis €" value={local.price_max} onChange={(e) => setLocal({ ...local, price_max: e.target.value })} />
          <input data-testid="filter-brand" className={input} placeholder="Marke" value={local.brand} onChange={(e) => setLocal({ ...local, brand: e.target.value })} />
          <div className="flex gap-2">
            <input data-testid="filter-size" className={input} placeholder="Größe" value={local.size} onChange={(e) => setLocal({ ...local, size: e.target.value })} />
            <button data-testid="apply-filters-btn" className="bg-primary text-primary-foreground text-sm font-bold px-4 rounded-xl shrink-0 hover:opacity-90">OK</button>
          </div>
        </form>
      )}

      {result === null ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
          {[...Array(10)].map((_, i) => <div key={i} className="aspect-[4/5] bg-muted rounded-2xl animate-pulse" />)}
        </div>
      ) : result.items.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-16 text-center" data-testid="search-empty">
          <p className="text-muted-foreground">Keine Artikel gefunden. Versuche andere Filter.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4" data-testid="search-result-count">{result.total} Artikel</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {result.items.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
          </div>
          {result.pages > 1 && (
            <div className="flex justify-center gap-2 mt-10">
              {[...Array(Math.min(result.pages, 10))].map((_, i) => (
                <button key={i} data-testid={`page-btn-${i + 1}`} onClick={() => update("page", String(i + 1))}
                  className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${page === i + 1 ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
